import React from "react";
import { Tag, Button, Space } from "antd";
import { Edit3, Key } from "lucide-react";
import { useQueryStates, parseAsInteger, parseAsString } from "nuqs";
import dayjs from "dayjs";
import DataTable from "../../components/DataTable";
import { useRegisteredUserList } from "../../../hooks/api/user/hooks";
import { IRegisteredUserListResponse } from "../../../types/api/users-manage";
import UserEditModal from "./UserEditModal";
import UserPasswordModal from "./UserPasswordModal";
import { useModalStore } from "../../../store/slices/modal";
import { MODAL_TYPES } from "../../../types/modal-types";

const UserList: React.FC = () => {
  // Single qData state object managed by nuqs useQueryStates
  const [qData, setQData] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    limit: parseAsInteger.withDefault(10),
    search: parseAsString.withDefault(""),
    status: parseAsString.withDefault(""),
    startDate: parseAsString.withDefault(""),
    endDate: parseAsString.withDefault(""),
  });

  const queryParams = {
    page: qData.page,
    limit: qData.limit,
    search: qData.search || undefined,
    status: qData.status || undefined,
    startDate: qData.startDate || undefined,
    endDate: qData.endDate || undefined,
  };

  const { type, data: modalUser, open, close } = useModalStore();

  const { data, isLoading, isError } = useRegisteredUserList(queryParams);

  const handleEdit = (user: IRegisteredUserListResponse) => {
    open(MODAL_TYPES.EDIT_USER, user);
  };

  const handleResetPassword = (user: IRegisteredUserListResponse) => {
    open(MODAL_TYPES.RESET_USER_PASSWORD, user);
  };

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (record: IRegisteredUserListResponse) => {
        const fullName =
          record.name ||
          `${record.firstName || ""} ${record.lastName || ""}`.trim() ||
          "N/A";
        return (
          <div>
            <div className="font-semibold text-slate-800">{fullName}</div>
            {record.username && (
              <div className="text-xs text-slate-400">@{record.username}</div>
            )}
          </div>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email?: string) => email || "-",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (phone?: string) => phone || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status?: boolean) => (
        <Tag color={status !== false ? "green" : "volcano"}>
          {status !== false ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Joined Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date?: string) => (date ? new Date(date).toLocaleDateString() : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: IRegisteredUserListResponse) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<Edit3 className="w-4 h-4 text-blue-600" />}
            onClick={() => handleEdit(record)}
            title="Edit User"
          />
          <Button
            type="text"
            icon={<Key className="w-4 h-4 text-amber-600" />}
            onClick={() => handleResetPassword(record)}
            title="Reset Password"
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6 min-h-screen">
      <DataTable
        title="User Management"
        dataSource={data?.data || []}
        columns={columns}
        loading={isLoading}
        isError={isError}
        rowKey={(record) => record._id || String(Math.random())}
        searchProps={{
          searchValue: qData.search,
          onSearchChange: (v) => setQData({ search: v, page: 1 }),
          placeholder: "Search by name, email, or username...",
          statusValue: qData.status,
          onStatusChange: (v) => setQData({ status: v, page: 1 }),
          showDateRangeFilter: true,
          dateRangeValue: [
            qData.startDate ? dayjs(qData.startDate) : null,
            qData.endDate ? dayjs(qData.endDate) : null,
          ],
          onDateRangeChange: (_, dateStrings) => {
            setQData({
              startDate: dateStrings[0] || "",
              endDate: dateStrings[1] || "",
              page: 1,
            });
          },
        }}
        paginationProps={{
          page: qData.page,
          limit: qData.limit,
          total: data?.extra?.total || 0,
          onChange: (newPage, newLimit) =>
            setQData(
              newLimit !== qData.limit ? { page: 1, limit: newLimit } : { page: newPage },
            ),
        }}
      />

      <UserEditModal
        isOpen={type === MODAL_TYPES.EDIT_USER}
        onClose={close}
        user={modalUser}
      />

      <UserPasswordModal
        isOpen={type === MODAL_TYPES.RESET_USER_PASSWORD}
        onClose={close}
        user={modalUser}
      />
    </div>
  );
};

export default UserList;
